import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar, Users, Plus, Edit2, Trash2, Clock, MapPin,
  CheckCircle2, XCircle, AlertTriangle, Search, Loader2, Hourglass, Crown,
  Repeat, Link as LinkIcon, FileText, Check, HelpCircle, X as XIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import logger from '../utils/logger';
import { useRealtimeEvent } from '../utils/realtime';

const ROLE_LABELS = { administrator: 'Admin', supervisor: 'Supervisor', scheduler: 'Scheduler', staff: 'Staff', employee: 'Staff' };
const ROLE_GROUPS = [
  { key: 'administrator', label: 'Administrators' },
  { key: 'supervisor', label: 'Supervisors' },
  { key: 'scheduler', label: 'Schedulers' },
  { key: 'staff', label: 'Staff' },
];
const SVC_LABEL = { day_program: 'Explore', employment: 'Employment', in_home: 'IHCS' };
const RECURRENCE_LABELS = { none: 'Does not repeat', weekly: 'Weekly', biweekly: 'Every 2 weeks', monthly: 'Monthly' };

export default function MeetingsPage() {
  const { api, user } = useAuth();
  const isApprover = user?.ceo_life_access === true;
  const isAdmin = user?.role === 'administrator';
  const canCreate = ['administrator', 'supervisor', 'scheduler'].includes(user?.role);

  const [meetings, setMeetings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [scope, setScope] = useState('upcoming');
  const [loading, setLoading] = useState(true);

  // Create / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', agenda: '', date: '', time_in: '10:00', time_out: '11:00', location: '', attendee_user_ids: [], notes: '', notes_url: '', recurrence_pattern: 'none', recurrence_count: 4 });
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState({});  // {user_id: [conflicts]}
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get('/meetings', { params: { scope } }),
        api.get('/employees').catch(() => ({ data: [] })),
      ]);
      setMeetings(m.data);
      setStaff(s.data.filter(e => e.is_active !== false));
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, scope]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useRealtimeEvent('meetings.changed', fetchData);

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', agenda: '', date: todayStr, time_in: '10:00', time_out: '11:00', location: '', attendee_user_ids: [], notes: '', notes_url: '', recurrence_pattern: 'none', recurrence_count: 4 });
    setConflicts({}); setStaffSearch('');
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      title: m.title, agenda: m.agenda || '',
      date: m.date, time_in: m.time_in, time_out: m.time_out, location: m.location || '',
      attendee_user_ids: (m.attendees || []).map(a => a.user_id),
      notes: m.notes || '',
      notes_url: m.notes_url || '',
      recurrence_pattern: 'none', recurrence_count: 1,
    });
    setConflicts({}); setStaffSearch('');
    setDialogOpen(true);
  };

  // Live conflict check — fires whenever date/time/attendees change
  useEffect(() => {
    if (!dialogOpen) return;
    if (!form.date || !form.time_in || !form.time_out || form.attendee_user_ids.length === 0) {
      setConflicts({}); return;
    }
    if (form.time_out <= form.time_in) return;
    const t = setTimeout(async () => {
      setConflictsLoading(true);
      try {
        const { data } = await api.post('/meetings/check-conflicts', {
          date: form.date, time_in: form.time_in, time_out: form.time_out,
          attendee_user_ids: form.attendee_user_ids,
        });
        const map = {};
        (data.attendees || []).forEach(a => { map[a.user_id] = a.conflicts; });
        setConflicts(map);
      } catch (e) { logger.error(e); }
      setConflictsLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [api, dialogOpen, form.date, form.time_in, form.time_out, form.attendee_user_ids]);

  const totalConflicts = useMemo(() => Object.values(conflicts).reduce((s, c) => s + (c?.length || 0), 0), [conflicts]);

  const toggleAttendee = (uid) => {
    setForm(p => ({
      ...p,
      attendee_user_ids: p.attendee_user_ids.includes(uid)
        ? p.attendee_user_ids.filter(x => x !== uid)
        : [...p.attendee_user_ids, uid],
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.time_out <= form.time_in) { toast.error('End time must be after start time'); return; }
    if (totalConflicts > 0) {
      const ok = window.confirm(`There are ${totalConflicts} shift conflicts. Save anyway?`);
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (editing) {
        // Recurrence cannot be edited on an existing meeting (each instance is independent).
        const { recurrence_pattern, recurrence_count, ...payload } = form;
        await api.put(`/meetings/${editing.id}`, payload);
        toast.success('Meeting updated');
      } else {
        const { data } = await api.post('/meetings', form);
        const seriesCount = (data.series_created_ids || []).length || 1;
        const seriesNote = seriesCount > 1 ? ` (${seriesCount} occurrences)` : '';
        if (data.requires_approval) toast.success(`Meeting created${seriesNote} — pending approval from ${data.attendees.find(a => a.role === 'administrator')?.name || 'admin'}`);
        else toast.success(`Meeting scheduled${seriesNote}`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    const isSeries = !!m.series_id;
    let scope = 'one';
    if (isSeries) {
      const choice = window.prompt(
        `"${m.title}" is part of a recurring series of ${m.series_total || 'multiple'} meetings.\n\nType:\n  1 — cancel this one only\n  2 — cancel this and future occurrences\n  3 — cancel the entire series\n\n(or anything else to abort)`,
        '1'
      );
      if (choice === '1') scope = 'one';
      else if (choice === '2') scope = 'future';
      else if (choice === '3') scope = 'series';
      else return;
    } else {
      if (!window.confirm(`Cancel "${m.title}"?`)) return;
    }
    try {
      const { data } = await api.delete(`/meetings/${m.id}`, { params: { scope } });
      toast.success(`${data.count > 1 ? `${data.count} meetings ` : 'Meeting '}cancelled`);
      fetchData();
    }
    catch (e) { toast.error('Failed to cancel'); }
  };

  const decide = async (m, status) => {
    try {
      await api.put(`/meetings/${m.id}/decision?status=${status}`);
      toast.success(status === 'approved' ? 'Approved' : 'Denied');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Decision failed'); }
  };

  const pushToGoogle = async (m) => {
    try {
      const { data } = await api.post(`/meetings/${m.id}/push-to-google`);
      toast.success(`Synced to Google Calendar${data.attendee_emails_invited ? ` · ${data.attendee_emails_invited} invitee${data.attendee_emails_invited === 1 ? '' : 's'} notified` : ''}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Push failed');
    }
  };

  const rsvp = async (m, status) => {
    try {
      await api.put(`/meetings/${m.id}/rsvp?status=${status}`);
      const labels = { accepted: 'Accepted', tentative: 'Marked tentative', declined: 'Declined' };
      toast.success(labels[status] || 'RSVP updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RSVP failed');
    }
  };

  const filteredStaff = useMemo(() => {
    if (!staffSearch) return staff;
    const q = staffSearch.toLowerCase();
    return staff.filter(s => (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [staff, staffSearch]);

  // Group attendees by role so the picker organizes Administrators, Supervisors,
  // Schedulers, and Staff into clearly labelled sections instead of one flat list.
  const groupedStaff = useMemo(() => {
    const groups = ROLE_GROUPS.map(g => ({ ...g, members: [] }));
    const seen = new Set();
    for (const s of filteredStaff) {
      const role = (s.role || '').toLowerCase();
      const target = groups.find(g => g.key === role || (g.key === 'staff' && (role === 'employee' || role === 'staff')));
      if (target && !seen.has(s.id)) { target.members.push(s); seen.add(s.id); }
    }
    // Alphabetize within each group
    for (const g of groups) g.members.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return groups.filter(g => g.members.length > 0);
  }, [filteredStaff]);

  const pendingCount = meetings.filter(m => m.approval_status === 'pending').length;

  return (
    <div className="p-4 sm:p-8 space-y-6 max-w-6xl" data-testid="meetings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Meetings</h1>
          <p className="text-sm text-[#5A4D6E] mt-1">Schedule internal meetings with conflict checking against the operational schedule.</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="create-meeting-btn">
            <Plus className="h-4 w-4" /> New Meeting
          </Button>
        )}
      </div>

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]">
          <TabsTrigger value="upcoming" data-testid="meetings-tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="pending" data-testid="meetings-tab-pending">
            Pending Approval {pendingCount > 0 && <Badge className="ml-1 bg-[#FFB74D] text-white">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="meetings-tab-past">Past</TabsTrigger>
        </TabsList>

        <TabsContent value={scope} className="mt-5">
          {loading ? (
            <div className="text-center py-12 text-[#8E85A0]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-12 text-[#8E85A0]">
              <Calendar className="h-10 w-10 mx-auto mb-2 text-[#DED8EB]" />
              <p className="text-sm">{scope === 'pending' ? 'Nothing pending approval' : `No ${scope} meetings`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {meetings.map(m => (
                <MeetingCard
                  key={m.id} m={m} user={user}
                  isApprover={isApprover} isAdmin={isAdmin}
                  onEdit={() => openEdit(m)} onDelete={() => handleDelete(m)}
                  onApprove={() => decide(m, 'approved')} onDeny={() => decide(m, 'denied')}
                  onPushGoogle={() => pushToGoogle(m)}
                  onRsvp={(status) => rsvp(m, status)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="meeting-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editing ? 'Edit Meeting' : 'Schedule Meeting'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Q2 staffing review" className="border-[#DED8EB]" data-testid="meeting-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="border-[#DED8EB]" data-testid="meeting-date" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Location</Label>
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                placeholder="Conf room / Zoom link / Address" className="border-[#DED8EB]" data-testid="meeting-location" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Start *</Label>
              <Input type="time" value={form.time_in} onChange={e => setForm(p => ({ ...p, time_in: e.target.value }))} className="border-[#DED8EB] font-mono" data-testid="meeting-start" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">End *</Label>
              <Input type="time" value={form.time_out} onChange={e => setForm(p => ({ ...p, time_out: e.target.value }))} className="border-[#DED8EB] font-mono" data-testid="meeting-end" />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Agenda</Label>
              <Textarea value={form.agenda} onChange={e => setForm(p => ({ ...p, agenda: e.target.value }))}
                placeholder="What we'll cover..." className="border-[#DED8EB] min-h-[60px]" data-testid="meeting-agenda" />
            </div>

            {/* Recurrence — only on create. Existing meetings can be edited per-instance. */}
            {!editing && (
              <div className="md:col-span-2 space-y-2">
                <Label className="text-[#2D1B4E] font-medium flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5" /> Repeats</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={form.recurrence_pattern}
                    onChange={e => setForm(p => ({ ...p, recurrence_pattern: e.target.value }))}
                    className="border border-[#DED8EB] rounded-md h-9 px-2 text-sm bg-white text-[#2D1B4E]"
                    data-testid="meeting-recurrence-pattern"
                  >
                    {Object.entries(RECURRENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {form.recurrence_pattern !== 'none' && (
                    <>
                      <span className="text-sm text-[#5A4D6E]">for</span>
                      <Input
                        type="number" min="1" max="26"
                        value={form.recurrence_count}
                        onChange={e => setForm(p => ({ ...p, recurrence_count: Math.max(1, Math.min(26, parseInt(e.target.value) || 1)) }))}
                        className="w-20 border-[#DED8EB] h-9"
                        data-testid="meeting-recurrence-count"
                      />
                      <span className="text-sm text-[#5A4D6E]">occurrences</span>
                    </>
                  )}
                </div>
                {form.recurrence_pattern !== 'none' && (
                  <p className="text-[11px] text-[#8E85A0]">Each occurrence is created as its own meeting. You can edit or cancel them individually, or cancel the entire series at once.</p>
                )}
              </div>
            )}

            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#2D1B4E] font-medium flex items-center gap-1.5"><LinkIcon className="h-3.5 w-3.5" /> Notes link <span className="font-normal text-[#8E85A0]">(optional)</span></Label>
              <Input
                value={form.notes_url}
                onChange={e => setForm(p => ({ ...p, notes_url: e.target.value }))}
                placeholder="https://docs.google.com/... or any URL to your meeting notes"
                className="border-[#DED8EB]"
                data-testid="meeting-notes-url"
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#2D1B4E] font-medium flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Quick notes — summary, decisions, action items…"
                className="border-[#DED8EB] min-h-[80px]"
                data-testid="meeting-notes"
              />
            </div>

            {/* Attendee picker with live conflict warnings */}
            <div className="md:col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[#2D1B4E] font-medium">Attendees ({form.attendee_user_ids.length})</Label>
                {totalConflicts > 0 && (
                  <Badge className="bg-[#FFE0B2] text-[#E65100] gap-1" data-testid="conflict-badge">
                    <AlertTriangle className="h-3 w-3" /> {totalConflicts} shift conflict{totalConflicts === 1 ? '' : 's'}
                  </Badge>
                )}
                {conflictsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#8E85A0]" />}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E85A0]" />
                <Input placeholder="Search staff..." value={staffSearch} onChange={e => setStaffSearch(e.target.value)}
                  className="pl-9 border-[#DED8EB] h-9" data-testid="attendee-search" />
              </div>
              <div className="max-h-56 overflow-y-auto border border-[#DED8EB] rounded-lg" data-testid="attendee-list">
                {groupedStaff.length === 0 && <p className="text-xs text-[#8E85A0] text-center py-3">No staff match</p>}
                {groupedStaff.map(group => (
                  <div key={group.key} className="border-b border-[#F0EDF6] last:border-b-0">
                    <div className="px-3 py-1 bg-[#F7F4EF] text-[10px] font-bold uppercase tracking-wider text-[#6B3FA0] sticky top-0" data-testid={`role-group-${group.key}`}>
                      {group.label} <span className="text-[#8E85A0] font-normal">· {group.members.length}</span>
                    </div>
                    {group.members.map(s => {
                      const checked = form.attendee_user_ids.includes(s.id);
                      const cs = conflicts[s.id] || [];
                      return (
                        <label key={s.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F7F4EF] ${checked ? 'bg-[#EDE7F6]/40' : ''}`} data-testid={`attendee-${s.id}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleAttendee(s.id)} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#2D1B4E] truncate">{s.name}</span>
                              <Badge className="text-[9px] bg-[#F0EDF6] text-[#5A4D6E]">{ROLE_LABELS[s.role] || s.role}</Badge>
                              {s.ceo_life_access && <Crown className="h-3 w-3 text-[#FFB74D]" title="CEO Life user — approval required" />}
                            </div>
                            {checked && cs.length > 0 && (
                              <div className="text-[11px] text-[#E65100] mt-0.5 flex items-center gap-1 flex-wrap">
                                <AlertTriangle className="h-3 w-3 shrink-0" />
                                {cs.map((c) => (
                                  <span key={c.shift_id} className="bg-[#FFE0B2] px-1.5 py-0.5 rounded">
                                    {SVC_LABEL[c.service_line] || c.service_line} {c.time_in}–{c.time_out}{c.is_draft ? ' (draft)' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="meeting-save-btn">
              {saving ? 'Saving…' : editing ? 'Update' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MeetingCard({ m, user, isApprover, isAdmin, onEdit, onDelete, onApprove, onDeny, onPushGoogle, onRsvp }) {
  const isOrganizer = m.organizer_id === user?.id;
  const canEdit = isOrganizer || isAdmin;
  const isPending = m.approval_status === 'pending';
  const isDenied = m.approval_status === 'denied';
  const synced = !!m.google_event_id;
  const dt = (() => { try { return format(parseISO(m.date), 'EEE, MMM d'); } catch { return m.date; } })();

  const attendees = m.attendees || [];
  const myAttendee = attendees.find(a => a.user_id === user?.id);
  const myStatus = myAttendee?.status || null;

  // RSVP rollup tallies
  const rollup = attendees.reduce((acc, a) => {
    const s = a.status || 'pending';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className={`border shadow-sm bg-white ${isPending ? 'border-[#FFB74D] border-l-4' : isDenied ? 'border-[#D9534F] border-l-4 opacity-70' : 'border-[#DED8EB]'}`} data-testid={`meeting-card-${m.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-[#2D1B4E]">{m.title}</h3>
              {m.series_id && (
                <Badge className="bg-[#EDE7F6] text-[#6B3FA0] gap-1" title={`Recurring meeting · ${m.series_index + 1} of ${m.series_total}`}>
                  <Repeat className="h-3 w-3" /> {m.series_index + 1}/{m.series_total}
                </Badge>
              )}
              {isPending && <Badge className="bg-[#FFE0B2] text-[#E65100] gap-1"><Hourglass className="h-3 w-3" />Pending</Badge>}
              {m.approval_status === 'approved' && <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>}
              {isDenied && <Badge className="bg-red-100 text-red-700 gap-1"><XCircle className="h-3 w-3" />Denied</Badge>}
              {m.notes_url && (
                <a href={m.notes_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E3F2FD] text-[#1565C0] hover:bg-[#BBDEFB]"
                  data-testid={`notes-link-${m.id}`}>
                  <LinkIcon className="h-3 w-3" /> Notes
                </a>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-[#5A4D6E] mt-1 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{dt}</span>
              <span className="flex items-center gap-1 font-mono"><Clock className="h-3.5 w-3.5" />{m.time_in}–{m.time_out}</span>
              {m.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{m.location}</span>}
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{attendees.length} attendee{attendees.length === 1 ? '' : 's'}</span>
              {attendees.length > 0 && (
                <span className="flex items-center gap-1.5 text-[11px]" data-testid={`rsvp-rollup-${m.id}`}>
                  {rollup.accepted > 0 && <span className="inline-flex items-center gap-0.5 text-green-700"><Check className="h-3 w-3" />{rollup.accepted}</span>}
                  {rollup.tentative > 0 && <span className="inline-flex items-center gap-0.5 text-[#E65100]"><HelpCircle className="h-3 w-3" />{rollup.tentative}</span>}
                  {rollup.declined > 0 && <span className="inline-flex items-center gap-0.5 text-[#D9534F]"><XIcon className="h-3 w-3" />{rollup.declined}</span>}
                  {(rollup.pending || 0) > 0 && <span className="inline-flex items-center gap-0.5 text-[#8E85A0]"><Hourglass className="h-3 w-3" />{rollup.pending}</span>}
                </span>
              )}
            </div>
            {m.agenda && <p className="text-xs text-[#5A4D6E] mt-2 line-clamp-2">{m.agenda}</p>}
            {m.notes && <p className="text-xs text-[#5A4D6E] mt-1.5 line-clamp-3 italic border-l-2 border-[#DED8EB] pl-2" data-testid={`notes-preview-${m.id}`}>{m.notes}</p>}
            <p className="text-[10px] text-[#8E85A0] mt-2">Organized by {m.organizer_name}{m.approved_by ? ` · Decided by ${m.approved_by}` : ''}</p>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {attendees.map(a => {
                  const s = a.status || 'pending';
                  const cls = s === 'accepted' ? 'bg-green-50 text-green-700 border border-green-200'
                    : s === 'tentative' ? 'bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D]'
                    : s === 'declined' ? 'bg-red-50 text-[#D9534F] border border-red-200 line-through opacity-70'
                    : 'bg-[#F0EDF6] text-[#5A4D6E]';
                  const icon = s === 'accepted' ? '✓' : s === 'tentative' ? '?' : s === 'declined' ? '✗' : '';
                  return (
                    <span key={a.user_id} className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${cls}`} title={`${a.name} — ${s}`}>
                      {icon && <span className="font-bold">{icon}</span>}{a.name}
                    </span>
                  );
                })}
              </div>
            )}
            {/* RSVP — visible only to invited attendees (not the organizer themselves) */}
            {myAttendee && !isOrganizer && !isDenied && (
              <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-[#F0EDF6]" data-testid={`rsvp-controls-${m.id}`}>
                <span className="text-[10px] text-[#8E85A0] uppercase tracking-wider">Your RSVP:</span>
                <Button onClick={() => onRsvp('accepted')} size="sm" variant={myStatus === 'accepted' ? 'default' : 'outline'}
                  className={`h-7 text-[11px] gap-1 ${myStatus === 'accepted' ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-[#DED8EB] text-green-700 hover:bg-green-50'}`}
                  data-testid={`rsvp-accept-${m.id}`}>
                  <Check className="h-3 w-3" /> Yes
                </Button>
                <Button onClick={() => onRsvp('tentative')} size="sm" variant={myStatus === 'tentative' ? 'default' : 'outline'}
                  className={`h-7 text-[11px] gap-1 ${myStatus === 'tentative' ? 'bg-[#E65100] hover:bg-[#BF360C] text-white' : 'border-[#DED8EB] text-[#E65100] hover:bg-[#FFF3E0]'}`}
                  data-testid={`rsvp-tentative-${m.id}`}>
                  <HelpCircle className="h-3 w-3" /> Maybe
                </Button>
                <Button onClick={() => onRsvp('declined')} size="sm" variant={myStatus === 'declined' ? 'default' : 'outline'}
                  className={`h-7 text-[11px] gap-1 ${myStatus === 'declined' ? 'bg-[#D9534F] hover:bg-[#C62828] text-white' : 'border-[#DED8EB] text-[#D9534F] hover:bg-red-50'}`}
                  data-testid={`rsvp-decline-${m.id}`}>
                  <XIcon className="h-3 w-3" /> No
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isPending && isApprover && (
              <>
                <Button onClick={onApprove} size="sm" className="h-8 bg-green-600 hover:bg-green-700 text-white gap-1" data-testid={`approve-${m.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
                <Button onClick={onDeny} size="sm" variant="outline" className="h-8 border-red-300 text-red-600 hover:bg-red-50 gap-1" data-testid={`deny-${m.id}`}>
                  <XCircle className="h-3.5 w-3.5" /> Deny
                </Button>
              </>
            )}
            {canEdit && !isDenied && (
              <Button
                onClick={onPushGoogle}
                size="sm"
                variant="outline"
                className={`h-8 gap-1 ${synced ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-[#DED8EB] text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
                data-testid={`push-google-${m.id}`}
                title={synced ? 'Update on Google Calendar (re-invites attendees)' : 'Push to Google Calendar (invites attendees with email)'}
              >
                <Calendar className="h-3.5 w-3.5" />
                {synced ? 'Synced' : 'To Google'}
              </Button>
            )}
            {synced && m.google_html_link && (
              <a href={m.google_html_link} target="_blank" rel="noreferrer"
                className="text-[10px] text-green-700 hover:underline px-1 self-center"
                data-testid={`google-link-${m.id}`}>Open ↗</a>
            )}
            {canEdit && (
              <>
                <Button onClick={onEdit} variant="ghost" size="icon" className="h-8 w-8 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-meeting-${m.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                <Button onClick={onDelete} variant="ghost" size="icon" className="h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-meeting-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
